import { useState } from "react";
import {
  Space,
  Button,
  Row,
  Col,
  notification,
  Typography,
  TabsProps,
  Tabs,
  Modal,
  Input,
  Tooltip
} from "antd";
import { BASE_URL } from "../../../redux/services/baseApi";

import type { ColumnsType } from "antd/es/table";
import {
  DollarOutlined,
  RollbackOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  EditOutlined,
  MailOutlined,
  PayCircleOutlined,
  CopyOutlined,
  FilePdfOutlined,
  FilePdfFilled,
  CloudUploadOutlined,
  CloudFilled,
  UploadOutlined
} from "@ant-design/icons";

import {
  useGeneratePaymentLinkMutation,
  useGetSurveysQuery,
  useUpdateSurveyMutation
} from "../../../redux/services/surveyAPI";
import {
  useCreatePaymentMutation,
  useCreateRefundMutation,
  useSendCustomerReportMutation,
  useSendQuotationPdfMutation,
  useViewCustomerReportMutation,
  useViewQuotationReportMutation
} from "../../../redux/services/assignmentApi";
import Filters from "../Filters";
import {
  getStatusText,
  localizeDateTime,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { useLocation } from "react-router";
import AddPaymentModal from "./AddPaymentModal";
import ErrorDetails from "../../../components/ErrorDetails";
import { Link, useNavigate } from "react-router-dom";
import PreviewAndSendEmailModal from "./PreviewAndSendEmailModal";
import { useAppSelector } from "../../../redux/hooks";
import { EMAIL_TYPES } from "../../../constants/helperConstants";
import UploadInvoicePdfModal from "../Survey/UploadInvoicePdfModal";
import TopScrollTable from "../../../components/TopScrollTable";

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "Unpaid Surveys"
  },
  {
    key: "2",
    label: "Paid Surveys"
  }
];

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

interface Survey {
  id: string;
  ecb_survey_code: string;
}

const { Text } = Typography;

const PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM = new Date("2025-03-01");

const Survey = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    payment_received: false,
    page: parseInt(querySearchParams.page) || 1,
    ecb_code: parseInt(querySearchParams.ecb_code),
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const auth = useAppSelector<any>((store) => store.auth);
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [paymentType, setPaymentType] = useState("");
  const [tabKey, setTabKey] = useState("1");
  const [emailType, setEmailType] = useState("seek_payment_email");
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [generatePaymentLink] = useGeneratePaymentLinkMutation();

  const [createPayment] = useCreatePaymentMutation();
  const [createRefund] = useCreateRefundMutation();

  const [api, contextHolder] = notification.useNotification();

  const { data, refetch, isLoading, isFetching } = useGetSurveysQuery({
    ...queryParams,
    include_all: true
  });
  const surveys = data?.surveys || [];

  const onShowAddPaymentModal = (survey: any, payType: string) => {
    setSelectedSurvey(survey);
    setPaymentType(payType);
    setShowModal(true);
  };

  const onShowEmailPreviewModal = (survey: any, eType: string) => {
    setEmailType(eType);
    setSelectedSurvey(survey);
    setShowEmailPreviewModal(true);
  };

  const onAddPayment = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      if (paymentType === "refund") {
        await createRefund(values).unwrap();
      } else if (paymentType === "payment") {
        await createPayment(values).unwrap();
      }
      api.success({
        message: `Add ${paymentType} to survey successful`,
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: `Add ${paymentType} to survey failed`,
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const [updateSurvey] = useUpdateSurveyMutation();

  const onSendEmail = async (values: any, setLoading: any, form: any, fileList: any) => {
    setLoading(true);

    try {
      const id = values["id"];
      const survey = new FormData();

      for (const key in values) {
        if (values?.[key]) {
          survey.append(`survey[${key}]`, values[key]);
        }
      }

      fileList.forEach((file: any) => {
        survey.append("survey[attachments][]", file);
      });

      const response = await fetch(`${BASE_URL}surveys/${id}/trigger_email`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: survey
      });

      const responseData = await response.json();

      if (responseData?.status === "Successful") {
        setLoading(false);
        api.success({
          message: "Email Sent!",
          placement: "top"
        });
        setShowEmailPreviewModal(false);
        setSelectedSurvey(null);
        refetch();
      } else {
        setLoading(false);
        api.info({
          message: "Email sending failed!",
          description: <ErrorDetails errors={responseData || []} />,
          placement: "top"
        });
      }
    } catch (error: any) {
      setLoading(false);
      api.info({
        message: "Email sending failed!",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onPaid = async (id: any) => {
    try {
      const values = { payment_received: true };
      const data = { ...values };
      await updateSurvey({ id, survey: data }).unwrap();
      api.success({
        message: "Survey marked paid successfully!",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update Survey failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onCancel = () => {
    setShowModal(false);
    setShowEmailPreviewModal(false);
    setShowInvoiceUploadModal(false);
    setSelectedSurvey(null);
    setPaymentType("");
  };

  const handleCopy = (value: string) => {
    try {
      navigator.clipboard.writeText(value);
      api.success({ message: "Copied to clipboard!" });
    } catch (err) {
      api.error({ message: "Failed to copy!" });
    }
  };

  const onGeneratePaymentLink = async (survey: any) => {
    try {
      const res = await generatePaymentLink(survey?.id).unwrap();
      if (res.payment_link) {
        Modal.info({
          title: `Payment link for ${survey?.ecb_survey_code}`,
          content: (
            <>
              <Input
                value={res.payment_link}
                disabled
                suffix={
                  <Tooltip title="Copy to clipboard">
                    <CopyOutlined
                      onClick={() => handleCopy(res.payment_link)}
                      style={{ cursor: "pointer", padding: "0 10px" }}
                    />
                  </Tooltip>
                }
              />
            </>
          ),
          okText: "Close",
          width: 600,
          onOk() {
            setSelectedSurvey(null);
          }
        });
      } else {
        api.error({
          message: "Payment link is not available"
        });
      }
    } catch (error: any) {
      api.error({
        message: "Payment link generation failed"
      });
    }
  };

  const onShowPaymentLinkModal = (survey: any) => {
    onGeneratePaymentLink(survey);
  };

  const [sendCustomerReport] = useSendCustomerReportMutation();
  const [onSendQuotationPdf] = useSendQuotationPdfMutation();
  const [onViewQuotationReport] = useViewQuotationReportMutation();
  const [viewCustomerReport] = useViewCustomerReportMutation();

  const onSendCustomerReport = async (surveyId: any) => {
    try {
      await sendCustomerReport(surveyId).unwrap();
      api.success({
        message: "Closure report send successfully",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.error({
        message: "Closure report send operation failed",
        placement: "top"
      });
    }
  };

  const onViewCustomerReport = async (surveyId: any) => {
    try {
      const res = await viewCustomerReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
      refetch();
    } catch (error: any) {
      api.error({
        message: "MSIL SR report operation failed",
        placement: "top"
      });
    }
  };

  const sendQuotationPdf = async (surveyId: any) => {
    try {
      await onSendQuotationPdf(surveyId).unwrap();
      api.success({
        message: "Send Quotation Pdf Operation Completd Successfully",
        placement: "top"
      });
    } catch (error: any) {
      api.info({
        message: "Send Quotation Pdf Operation failed",
        placement: "top"
      });
    }
  };

  const viewQuotationReport = async (surveyId: any) => {
    try {
      const res = await onViewQuotationReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
    } catch (error: any) {
      api.info({
        message: "View Quotation Operation failed",
        placement: "top"
      });
    }
  };

  const onShowInvoiceUploadModal = (survey: any) => {
    setSelectedSurvey(survey);
    setShowInvoiceUploadModal(true);
  };

  const onUploadInvoicePdf = async (values: any, setLoading: any, form: any, fileList: any) => {
    setLoading(true);

    try {
      const id = values["id"];
      const survey = new FormData();

      for (const key in values) {
        if (values?.[key]) {
          survey.append(`survey[${key}]`, values[key]);
        }
      }

      fileList.forEach((file: any) => {
        survey.append("survey[attachment]", file);
      });

      const response = await fetch(`${BASE_URL}print_reports/${id}/upload_invoice_pdf`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: survey
      });

      const responseData = await response.json();

      if (responseData?.pdf_url) {
        setLoading(false);
        api.success({
          message: "Invoice Pdf Upload Successful!",
          placement: "top"
        });
        setShowInvoiceUploadModal(false);
        setSelectedSurvey(null);
        refetch();
      } else {
        setLoading(false);
        setShowInvoiceUploadModal(false);
        setSelectedSurvey(null);
        api.error({
          message: "Invoice PDF upload failed",
          placement: "top"
        });
      }
    } catch (error: any) {
      setLoading(false);
      setShowInvoiceUploadModal(false);
      setSelectedSurvey(null);
      api.error({
        message: "Invoice PDF upload failed",
        placement: "top"
      });
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Survey Date",
      dataIndex: "scheduled_survey_date_time",
      key: "scheduled_survey_date_time",
      render: (scheduled_survey_date_time) => (
        <Text>
          {scheduled_survey_date_time ? localizeDateTime(scheduled_survey_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      render: (brand) => <>{brand?.name}</>
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Survey Code",
      dataIndex: "ecb_survey_code",
      key: "ecb_survey_code"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => <>{assigned_city_manager?.name || "-"}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "MSIL SR Sending Status",
      dataIndex: "survey_report_sent_to_api",
      key: "survey_report_sent_to_api",
      render: (_, survey: any) => (
        <>
          {survey?.brand?.send_reports_to_api && survey?.status === "completed"
            ? survey?.survey_report_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "MSIL Quote Sending Status",
      dataIndex: "quotation_sent_to_api",
      key: "quotation_sent_to_api",
      render: (_, survey: any) => (
        <>
          {survey?.brand?.send_reports_to_api && survey?.status === "completed"
            ? survey?.quotation_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "Partner",
      dataIndex: "survey_partner",
      key: "survey_partner",
      render: (survey_partner) => <>{survey_partner?.name || "-"}</>
    },

    {
      title: "Field Agent",
      dataIndex: "assigned_field_agent1",
      key: "assigned_field_agent1",
      render: (assigned_field_agent1) => <>{assigned_field_agent1?.name || "-"}</>
    },
    {
      title: "Survey Status",
      dataIndex: "status",
      key: "status",
      render: (_, survey: any) => <>{getStatusText(survey)}</>
    },
    {
      title: "Customer Rating",
      dataIndex: "customer_rating",
      key: "customer_rating",
      render: (_, survey: any) => (
        <>
          {survey.customer_rating && survey?.status === "completed"
            ? `${survey.customer_rating}/5`
            : "-"}
        </>
      )
    },
    {
      title: "Base Amount",
      dataIndex: "base_amount",
      key: "base_amount"
    },
    {
      title: "GST Amount",
      dataIndex: "gst_amount",
      key: "gst_amount"
    },
    {
      title: "Amount to be paid by customer",
      dataIndex: "invoice_total",
      key: "invoice_total"
    },
    {
      title: "Amount To Receive",
      dataIndex: "amount_to_receive",
      key: "amount_to_receive"
    },
    {
      title: "Seek payment email sent?",
      dataIndex: "seek_payment_email_sent",
      key: "seek_payment_email_sent",
      render: (_, survey: any) => (
        <>
          {new Date(survey.created_at) < PAYMENT_AND_EMAIL_STATUS_AVAILABLE_FROM
            ? ""
            : survey.seek_payment_email_sent
            ? "Yes"
            : "No"}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, survey: any) => (
        <Space size="middle">
          {tabKey === "1" && (
            <>
              <Button
                icon={<DollarOutlined />}
                onClick={() => onShowAddPaymentModal(survey, "payment")}
                title="Add Payment"
                disabled={survey?.payment_count < 3 ? false : true}
              ></Button>
              <Button
                icon={<RollbackOutlined />}
                onClick={() => onShowAddPaymentModal(survey, "refund")}
                title="Add Refund"
              ></Button>
              <Button
                icon={<CheckCircleOutlined />}
                title="Mark Paid"
                onClick={() => {
                  Modal.confirm({
                    title: "Confirm",
                    content: `Mark paid ${survey.ecb_survey_code} ?`,
                    okText: "Yes",
                    onOk() {
                      onPaid(survey.id);
                    }
                  });
                }}
              ></Button>
              <Link to={`${survey?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Link to={`${survey?.id}/update`}>
                <Button icon={<EditOutlined />} title="Update"></Button>
              </Link>
              <Button
                icon={<MailOutlined />}
                onClick={() => onShowEmailPreviewModal(survey, "seek_payment_email")}
                title={survey.pricing_expired ? "Pricing has Expired" : "Send Seek Payment Email"}
                disabled={survey.pricing_expired}
              ></Button>
              <Button
                icon={<MailOutlined />}
                onClick={() => onShowEmailPreviewModal(survey, "refund_email")}
                title={survey.pricing_expired ? "Pricing has Expired" : "Send Refund Email"}
                disabled={survey.pricing_expired}
              ></Button>
              <Button
                icon={<PayCircleOutlined />}
                title="Generate Payment Link"
                onClick={() => onShowPaymentLinkModal(survey)}
              ></Button>
            </>
          )}
          {tabKey === "2" && (
            <>
              <Link to={`${survey?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Link to={`${survey?.id}/update`}>
                <Button icon={<EditOutlined />} title="Update"></Button>
              </Link>
              <Button
                icon={<MailOutlined />}
                onClick={() => onShowEmailPreviewModal(survey, "refund_processed_email")}
                title="Send Refund Processed Email"
              ></Button>
            </>
          )}
          {survey?.brand?.send_reports_to_api && (
            <>
              <Button
                icon={<FilePdfOutlined />}
                title="MSIL SR report PDF"
                onClick={() => onViewCustomerReport(survey.id)}
              ></Button>
              <Button
                icon={<CloudUploadOutlined />}
                title="Send MSIL SR report"
                onClick={() => onSendCustomerReport(survey.id)}
              ></Button>
              <Button
                icon={<FilePdfFilled />}
                title="View Quotation PDF"
                onClick={() => viewQuotationReport(survey.id)}
              ></Button>
              <Button
                icon={<CloudFilled />}
                title="Send Quotation PDF"
                onClick={() => sendQuotationPdf(survey.id)}
              ></Button>
              <Button
                icon={<UploadOutlined />}
                title="Upload Invoice PDF"
                onClick={() => onShowInvoiceUploadModal(survey)}
              ></Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const onChange = (key: string) => {
    setTabKey(key);
    if (key === "1") {
      const values: any = { payment_received: false };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "2") {
      const values: any = { payment_received: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Surveys</h2>
            <Text>Total surveys: {data?.meta?.total_count || 0}</Text>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: "10px" // space between buttons
            }}
          >
            <Space size="middle" wrap>
              <Link to="upload-invoices">
                <Button size="large" type="primary">
                  Upload Invoices
                </Button>
              </Link>
              <Link to="import-payment-details">
                <Button size="large" type="primary">
                  Upload Payment Details
                </Button>
              </Link>
              <Link to="upload-survey-invoice-claim">
                <Button size="large" type="primary">
                  Upload Survey Invoice Claimed Date
                </Button>
              </Link>
              <Link to="upload-resurvey-invoice-claim">
                <Button size="large" type="primary">
                  Upload Re-Survey Invoice Claimed Date
                </Button>
              </Link>
            </Space>
          </div>
        </Col>
      </Row>
      <Tabs defaultActiveKey={tabKey} items={items} onChange={onChange} />
      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        surveys
      />
      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={surveys}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />
      <AddPaymentModal
        isModalOpen={showModal}
        headerText={`Survey: ${selectedSurvey?.ecb_survey_code}`}
        assignment={selectedSurvey}
        isRefund={paymentType === "refund"}
        onCancel={onCancel}
        onAddPayment={onAddPayment}
        title="Add"
      />
      {showEmailPreviewModal && (
        <PreviewAndSendEmailModal
          isModalOpen={showEmailPreviewModal}
          assignment={selectedSurvey}
          user={auth?.user}
          title={EMAIL_TYPES[emailType]}
          emailType={emailType}
          onCancel={onCancel}
          onSendEmail={onSendEmail}
        />
      )}

      {showInvoiceUploadModal && (
        <UploadInvoicePdfModal
          isModalOpen={showInvoiceUploadModal}
          assignment={selectedSurvey}
          onCancel={onCancel}
          onUploadInvoicePdf={onUploadInvoicePdf}
        />
      )}
    </>
  );
};

export default Survey;
